import { action, computed, observable, makeObservable } from 'mobx';
import { clientId, logger } from '../../../lib/helpers';
import {
  contentKey,
  scopedContentKey,
  X9ContentStateStore
} from '../../content/x9/common/X9ContentStateStore';
import { ClassroomKvEntry, ClassroomKvEntryUpdateModel } from '../models/classroom-models';
import mitt, { Emitter } from 'mitt';
import { debounce } from 'lodash';

const currentAuthor = clientId;

interface Item {
  readonly author: number;
  readonly changeStamp: number;
  readonly key: string;
  readonly json: string;
  readonly value: any;
}

interface NewItem {
  readonly key: string;
  readonly json: string;
  readonly value: any;
}

function entryWithValue(entry: ClassroomKvEntry, value: any): Item {
  return {
    author: entry.Author,
    changeStamp: entry.ChangeStamp,
    key: entry.Key,
    json: entry.Value,
    value: value
  };
}

function deserialize(entry: ClassroomKvEntry): Item {
  const json = entry.Value;

  if (json === null || json === undefined)
    return entryWithValue(entry, null);
  try {
    const value = JSON.parse(entry.Value);
    return entryWithValue(entry, value);
  } catch (e) {
    logger.error('ClassroomKeyValuesStore: error encountered parsing json',
      'key=', entry.Key, 'value=', json, 'exception=', e);
    return entryWithValue(entry, null);
  }
}

function serialize(key: string, value: any): NewItem {
  const json = JSON.stringify(value);
  return {
    key: key,
    json: json,
    value: value
  };
}

const revealedBlockIdsKey = 'revealed-blocks';
const homeworkBlockIdsKey = 'homework-blocks';

export class ClassroomKeyValuesStore implements X9ContentStateStore {
  @observable
  initialized: boolean;

  // entries received from backend
  @observable.shallow
  committed: Map<string, Item>;
  // entries to be sent to backend
  @observable.shallow
  changes: Map<string, NewItem>;

  @observable
  changeStamps: Set<number>;

  @observable
  missingChangeStamps: Set<number>;

  public readonly mitt: Emitter<any> = mitt();

  constructor() {
    makeObservable(this);
    this.initialized = false;
    this.committed = new Map<string, Item>();
    this.changes = new Map<string, NewItem>();
    this.changeStamps = new Set<number>();
    this.missingChangeStamps = new Set<number>();
  }

  @action
  clear = () => {
    this.initialized = false;
    this.committed.clear();
    this.changes.clear();
    this.changeStamps.clear();
    this.missingChangeStamps.clear();
  };

  @action
  mergeComplete(entries: readonly ClassroomKvEntry[]) {
    this.clear();
    for (let entry of entries) {
      this.committed.set(entry.Key, deserialize(entry));
      this.changeStamps.add(entry.ChangeStamp);
    }
    this.initialized = true;
  }

  @action
  mergePartial(changeStamps: readonly number[], entries: readonly ClassroomKvEntry[]) {
    if (!this.initialized)
      return;

    const changeStampsSet = new Set<number>(changeStamps);

    // some changeStamps were marked missing.
    // verify that they are indeed missing
    const missingChangeStamps = Array.from(this.missingChangeStamps);
    for (let missingChangeStamp of missingChangeStamps)
      if (!changeStampsSet.has(missingChangeStamp))
        this.missingChangeStamps.delete(missingChangeStamp);

    for (let changeStamp of entries.map(x => x.ChangeStamp)) {
      this.changeStamps.add(changeStamp);
      this.missingChangeStamps.delete(changeStamp);
    }

    for (let changeStamp of changeStampsSet)
      if (!this.changeStamps.has(changeStamp))
        this.missingChangeStamps.add(changeStamp);

    for (let entry of entries)
      this.mergeItem(deserialize(entry));
  }

  readonly homeworkMergeCompleted = debounce(() => {
    this.mitt.emit("homework-merge-completed");
  }, 400);

  readonly revealMergeCompleted = debounce(() => {
    this.mitt.emit("reveal-merge-completed");
  }, 400);

  private mergeItem(item: Item) {
    const key = item.key;
    const committed = this.committed;
    const changes = this.changes;

    const existing = committed.get(key);
    if (existing !== undefined && existing.changeStamp >= item.changeStamp)
      return;
    committed.set(key, item);

    const change = changes.get(key);
    if (change === undefined)
      return;
    if (item.author === currentAuthor && change.json !== item.json) {
      // don't overwrite pending changes
    }
    // new value is overriding committed.
    changes.delete(key);
    if (key == homeworkBlockIdsKey) {
      this.homeworkMergeCompleted();
    } else if (key == revealedBlockIdsKey) {
      this.revealMergeCompleted();
    }
  }

  get<T>(key: string): T | null {
    let entry = this.changes.get(key);
    if (entry !== undefined)
      return entry.value;

    entry = this.committed.get(key);
    if (entry !== undefined)
      return entry.value;

    return null;
  }

  @action
  set<T>(key: string, value: T) {
    if (!this.initialized)
      return;
    if (value === null || value === undefined)
      return;
    const newEntry = serialize(key, value);
    const existing = this.committed.get(key);
    if (existing !== undefined && existing.json === newEntry.json)
      return;
    this.changes.set(key, newEntry);
  }

  getContentState<T>(id: number): T | null {
    return this.get<T>(contentKey(id));
  }

  @action
  setContentState<T>(id: number, state: T) {
    this.set(contentKey(id), state);
  }

  getScopedContentState<T>(id: number, scope: string | number): T | null {
    return this.get<T>(scopedContentKey(id, scope));
  }

  @action
  setScopedContentState<T>(id: number, scope: string | number, state: T): void {
    this.set(scopedContentKey(id, scope), state);
  }

  @computed
  get allChanges(): ClassroomKvEntryUpdateModel[] {
    const result: ClassroomKvEntryUpdateModel[] = [];
    for (let entry of this.changes.values()) {
      result.push({
        Author: currentAuthor,
        Key: entry.key,
        Value: entry.json
      });
    }
    return result;
  }

  @computed
  get revealedBlockIds(): ReadonlySet<number> {
    const result = this.get<readonly number[]>(revealedBlockIdsKey);
    if (result === null)
      return new Set<number>();
    return new Set(result);
  }

  @computed
  get homeworkBlockIds(): ReadonlySet<number> {
    const result = this.get<readonly number[]>(homeworkBlockIdsKey);
    if (result === null)
      return new Set<number>();
    return new Set(result);
  }

  @action
  revealBlockId(blockId: number) {
    if (this.revealedBlockIds.has(blockId))
      return;
    const revealedBlockIds = Array.from(this.revealedBlockIds);
    revealedBlockIds.push(blockId);
    this.set(revealedBlockIdsKey, revealedBlockIds);
  }

  @action
  addBlockIdHomework(blockId: number) {
    if (this.homeworkBlockIds.has(blockId))
      return;
    const arr = Array.from(this.homeworkBlockIds);
    arr.push(blockId);
    this.set(homeworkBlockIdsKey, arr);
  }

  @action
  removeBlockIdHomework(blockId: number) {
    if (!this.homeworkBlockIds.has(blockId))
      return;
    const arr = Array.from(this.homeworkBlockIds)
      .filter(x => x != blockId);
    this.set(homeworkBlockIdsKey, arr);
  }
}
